import React from 'react'
import styles from './TestResults.module.css'
import TestingControls from './TestingControls'
import AccuracySummary from './AccuracySummary'
import DisagreementCard from './DisagreementCard'

interface TestResultsProps {
  testCount: number | ''
  setTestCount: (value: number | '') => void
  hasTestedBefore: boolean
  isTestingPrompt: boolean
  promptText: string
  testResults: any[] | null
  testSummary: any
  tokenSummary: any
  testDateRange: { earliest: string; latest: string } | null
  showResults: boolean
  correctedAudits: Set<string>
  resultsRef: React.RefObject<HTMLDivElement>
  onTestPrompt: (useNewAudits: boolean) => void
  onImageClick: (url: string) => void
  onMarkAICorrect: (auditObjectId: string) => void
  getHumanDefects: (humanResponse: any) => string
}

function TestResults({
  testCount,
  setTestCount,
  hasTestedBefore,
  isTestingPrompt,
  promptText,
  testResults,
  testSummary,
  tokenSummary,
  testDateRange,
  showResults,
  correctedAudits,
  resultsRef,
  onTestPrompt,
  onImageClick,
  onMarkAICorrect,
  getHumanDefects,
}: TestResultsProps) {
  return (
    <div
      ref={resultsRef}
      className={styles.resultsSection}
    >
      <div className={styles.resultsContent}>
        {/* Header with Testing Controls */}
        <div className={styles.resultsHeader}>
          <div className={styles.titleSection}>
            <h2>Test Prompt</h2>
            <p className={styles.testPromptSubtitle}>
              Use this section to test against historical data
            </p>
          </div>

          {/* Testing Controls (Right Side) */}
          <TestingControls
            testCount={testCount}
            setTestCount={setTestCount}
            hasTestedBefore={hasTestedBefore}
            isTestingPrompt={isTestingPrompt}
            promptText={promptText}
            onTestPrompt={onTestPrompt}
          />
        </div>

        {showResults && testResults ? (
          <>
            <AccuracySummary
              testResults={testResults}
              testSummary={testSummary}
              tokenSummary={tokenSummary}
              testDateRange={testDateRange}
              correctedAudits={correctedAudits}
            />

            <div className={styles.disagreementsSection}>
              {(() => {
                const successfulTests = testResults.filter((r) => !r.error)

                // Account for manual corrections when calculating metrics
                const isCorrect = (result: any) => {
                  // If originally incorrect but manually corrected, count as correct
                  if (
                    !result.correct &&
                    correctedAudits.has(result.auditObjectId)
                  ) {
                    return true
                  }
                  // If originally correct but manually un-corrected, count as incorrect
                  if (
                    result.correct &&
                    correctedAudits.has(result.auditObjectId)
                  ) {
                    return false
                  }
                  // Otherwise use original correctness
                  return result.correct
                }

                const disagreements = successfulTests.filter(
                  (r) => !isCorrect(r),
                )

                return (
                  <>
                    <h3>Disagreements ({disagreements.length})</h3>
                    {disagreements.length === 0 ? (
                      <p className={styles.noDisagreements}>
                        Perfect match! AI agreed with all human decisions.
                      </p>
                    ) : (
                      <div className={styles.disagreementsList}>
                        {disagreements.map((result, idx) => (
                          <DisagreementCard
                            key={idx}
                            result={result}
                            correctedAudits={correctedAudits}
                            onImageClick={onImageClick}
                            onMarkAICorrect={onMarkAICorrect}
                            getHumanDefects={getHumanDefects}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )
              })()}
            </div>
          </>
        ) : (
          <div className={styles.emptyTestState}>
            <p>Run a test to see results here</p>
            <p className={styles.emptyTestHint}>
              Configure your prompt above, then click "Test Prompt" to analyze{' '}
              {testCount} pass + {testCount} fail audits
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default TestResults
